import {useCallback, useMemo, useRef, useState} from "react";
import {ADataGridFilter} from "@atiautomacao/ati-ui-library";
import {Storage} from "../../../Utils/StorageUtils";

// Chave própria: a Auditoria não compartilha filtros avançados com a tela de Alarmes do Dashboard
export const AUDIT_ALARM_ADVANCED_FILTERS_KEY = 'alarm_audit_advanced_filters';

const readStoredFilters = (): ADataGridFilter[] => {
    const stored = Storage.local.get(AUDIT_ALARM_ADVANCED_FILTERS_KEY, null);
    return Array.isArray(stored) ? stored as ADataGridFilter[] : [];
};

// Cada valor selecionado conta como um filtro aplicado (valores de um mesmo filtro vêm unidos por ~)
export const countAdvancedFilters = (filters: ADataGridFilter[]): number =>
    filters.reduce((total, filter) => total + String(filter.value ?? '').split('~').filter(Boolean).length, 0);

/**
 * Estado dos filtros avançados (Equipamento, Situação e Severidade) da Auditoria de Alarmes.
 * @param onFiltersChanged chamado só quando o conjunto de filtros mudou de fato, para a tela voltar à primeira página
 */
export function useAuditAdvancedFilters(onFiltersChanged: () => void) {
    const [advancedFilters, setAdvancedFilters] = useState<ADataGridFilter[]>(readStoredFilters);
    const [sideBarOpen, setSideBarOpen] = useState(false);
    const [isClearFilters, setIsClearFilters] = useState(false);
    // Valor corrente fora do ciclo de render: updaters de setState precisam ser puros (StrictMode os executa 2x)
    const currentFiltersRef = useRef(advancedFilters);

    const appliedFiltersCount = useMemo(() => countAdvancedFilters(advancedFilters), [advancedFilters]);

    const commitFilters = useCallback((next: ADataGridFilter[]) => {
        if (JSON.stringify(currentFiltersRef.current) === JSON.stringify(next)) return;
        currentFiltersRef.current = next;
        setAdvancedFilters(next);
        if (next.length > 0) {
            Storage.local.set(AUDIT_ALARM_ADVANCED_FILTERS_KEY, next);
        } else {
            Storage.local.remove(AUDIT_ALARM_ADVANCED_FILTERS_KEY);
        }
        onFiltersChanged();
    }, [onFiltersChanged]);

    const applyFilters = useCallback((filters: ADataGridFilter[]) => {
        commitFilters(filters);
        setSideBarOpen(false);
        setIsClearFilters(false);
    }, [commitFilters]);

    const clearFilters = useCallback(() => {
        commitFilters([]);
        setIsClearFilters(true);
    }, [commitFilters]);

    const openSideBar = useCallback(() => setSideBarOpen(true), []);
    const closeSideBar = useCallback(() => setSideBarOpen(false), []);

    return {
        advancedFilters,
        appliedFiltersCount,
        sideBarOpen,
        openSideBar,
        closeSideBar,
        isClearFilters,
        applyFilters,
        clearFilters,
    };
}
